import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, Save, FileText } from "lucide-react";
import { RichTextEditor } from "@/components/RichTextEditor";
import { getLegal, saveLegal } from "@/lib/api";

const PAGES = [
  { key: "privacy", label: "Privacy Policy", path: "/privacy" },
  { key: "terms", label: "Terms of Service", path: "/terms" },
];

const fmt = (d) => {
  if (!d) return "never";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const Editor = ({ page }) => {
  const [html, setHtml] = useState("");
  const [updated, setUpdated] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getLegal(page.key)
      .then((d) => { setHtml(d.html || ""); setUpdated(d.updated_at || ""); })
      .catch(() => toast.error(`Couldn't load ${page.label}.`))
      .finally(() => setLoading(false));
  }, [page.key, page.label]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await saveLegal(page.key, html);
      setUpdated(r.updated_at);
      toast.success(`${page.label} saved — the public page is updated.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-8" data-testid={`legal-editor-${page.key}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-ink">
          <FileText className="h-5 w-5 text-clay" />
          <h2 className="font-heading text-xl">{page.label}</h2>
        </div>
        <span className="text-xs text-ink-faint">Last updated: {fmt(updated)}</span>
      </div>
      {loading ? (
        <div className="grid place-items-center py-12"><Loader2 className="h-6 w-6 animate-spin text-clay" /></div>
      ) : (
        <>
          <div className="mt-5">
            <RichTextEditor value={html} onChange={setHtml} />
          </div>
          <div className="mt-4 flex justify-end">
            <Button data-testid={`save-legal-${page.key}`} onClick={save} disabled={saving} className="bg-clay hover:bg-clay-hover text-white rounded-lg px-7 py-5 font-medium">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Save {page.label}</>)}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export const LegalPanel = () => (
  <TabsContent value="legal" className="mt-6 space-y-6" data-testid="legal-panel">
    <p className="text-sm text-ink-muted max-w-2xl">
      Edit the public Privacy Policy and Terms of Service. Saving updates the live page and its
      "last updated" date. The page layout, footer and links stay intact.
    </p>
    {PAGES.map((p) => <Editor key={p.key} page={p} />)}
  </TabsContent>
);
